import React, { useState, useEffect, useRef } from 'react';
import { useGMB } from '../contexts/GMBContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toolsAPI, postsAPI } from '../lib/api';
import api, { templateAPI } from '../lib/api';
import { 
  FileText, Copy, Check, Save, Calendar, Clock, 
  Image as ImageIcon, Sparkles, RefreshCw, Send, Upload,
  Pencil, ChevronRight, Phone, ExternalLink, ShoppingBag,
  CalendarPlus, UserPlus, X, ArrowLeft, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const CTA_OPTIONS = [
  { value: 'none', label: 'None', icon: null },
  { value: 'learn_more', label: 'Learn more', icon: <ExternalLink className="w-4 h-4" /> },
  { value: 'book', label: 'Book a visit', icon: <CalendarPlus className="w-4 h-4" /> },
  { value: 'order', label: 'Place an order', icon: <ShoppingBag className="w-4 h-4" /> },
  { value: 'shop', label: 'Shop', icon: <ShoppingBag className="w-4 h-4" /> },
  { value: 'sign_up', label: 'Sign up', icon: <UserPlus className="w-4 h-4" /> },
  { value: 'call', label: 'Call', icon: <Phone className="w-4 h-4" /> },
];

const POST_TYPES = [
  { value: 'standard', label: 'Standard Post', desc: 'Share an update about your business' },
  { value: 'event', label: 'Event', desc: 'Promote an upcoming event' },
  { value: 'offer', label: 'Offer', desc: 'Highlight a special deal or promotion' },
];

const TONES = ['Professional', 'Friendly', 'Promotional', 'Informative', 'Casual'];
const LANGUAGES = ['English', 'Hindi', 'Spanish', 'French', 'German'];

const PostGeneratorPage = () => {
  const { language } = useLanguage();
  const { gmbProfile, isConnected, connectionMethod, locations, publishToGMB } = useGMB();

  const [step, setStep] = useState(1);
  const [postType, setPostType] = useState('standard');
  const [contentMode, setContentMode] = useState('ai'); // 'ai' or 'manual'
  const [manualContent, setManualContent] = useState('');
  const [aiTopic, setAiTopic] = useState('');
  const [tone, setTone] = useState('Professional');
  const [lang, setLang] = useState(language === 'hi' ? 'Hindi' : 'English');
  const [cta, setCta] = useState('none');
  const [ctaUrl, setCtaUrl] = useState('');
  const [imageMode, setImageMode] = useState('ai'); // 'ai', 'upload', 'template', 'none'
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [regeneratingImage, setRegeneratingImage] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateGenerating, setTemplateGenerating] = useState(false);
  const fileInputRef = useRef(null);

  const contentText = contentMode === 'manual' ? manualContent : (generatedPost?.content || '');
  const charCount = contentText.length;
  const wordCount = contentText.trim() ? contentText.trim().split(/\s+/).length : 0;
  const ctaLabel = CTA_OPTIONS.find(c => c.value === cta)?.label || '';

  // Fetch user's selected templates for the template image option
  useEffect(() => {
    templateAPI.getSelected().then(res => {
      setSelectedTemplates(res.data.templates || []);
    }).catch(() => {});
  }, []);

  const handleGenerateTemplateImage = async () => {
    if (!selectedTemplateId) { toast.error('Select a template first'); return; }
    setTemplateGenerating(true);
    try {
      const res = await templateAPI.generate({
        template_id: selectedTemplateId,
        post_content: contentMode === 'manual' ? manualContent : aiTopic,
        headline: contentMode === 'manual' ? manualContent.split('\n')[0]?.slice(0, 50) : aiTopic.slice(0, 50),
        offer_text: postType === 'offer' ? (contentMode === 'manual' ? manualContent : aiTopic) : '',
      });
      if (res.data.image_url) {
        setUploadedImageUrl(res.data.image_url);
        toast.success('Creative image generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate creative');
    } finally { setTemplateGenerating(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadedImageUrl(res.data.image_url);
      setImageMode('upload');
      toast.success('Image uploaded!');
    } catch { toast.error('Failed to upload image'); }
    finally { setUploading(false); }
  };

  const handleGenerate = async () => {
    if (contentMode === 'ai' && !aiTopic.trim()) {
      toast.error('Please enter a topic or message for AI to generate');
      return;
    }
    if (contentMode === 'manual' && !manualContent.trim()) {
      toast.error('Please write your post content');
      return;
    }

    setLoading(true);
    setGeneratedPost(null);
    try {
      const payload = {
        business_type: gmbProfile?.category || '',
        offer: aiTopic,
        city: gmbProfile?.city || '',
        tone, language: lang,
        post_type: postType,
        generate_image: imageMode === 'ai',
        cta, cta_url: ctaUrl,
        content_mode: contentMode,
        manual_content: contentMode === 'manual' ? manualContent : '',
      };
      const res = await toolsAPI.generatePosts(payload);
      const posts = res.data.posts || [];
      if (posts.length > 0) {
        const post = posts[0];
        if (imageMode === 'upload' && uploadedImageUrl) {
          post.image_url = uploadedImageUrl;
        } else if (imageMode === 'template' && uploadedImageUrl) {
          post.image_url = uploadedImageUrl;
        }
        setGeneratedPost(post);
        setStep(3);
        toast.success('Post created!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate post');
    } finally { setLoading(false); }
  };

  const handleRegenerateImage = async () => {
    if (!generatedPost) return;
    setRegeneratingImage(true);
    try {
      const res = await toolsAPI.generateImage({
        prompt: generatedPost.content || generatedPost.title,
        business_type: gmbProfile?.category || 'Business',
        city: gmbProfile?.city || 'City'
      });
      if (res.data.image_url) {
        setGeneratedPost({ ...generatedPost, image_url: res.data.image_url });
        toast.success('New image generated!');
      }
    } catch { toast.error('Failed to regenerate image'); }
    finally { setRegeneratingImage(false); }
  };

  const handleSavePost = async (scheduled = false) => {
    if (!generatedPost) return;
    try {
      await postsAPI.schedule({
        business_type: gmbProfile?.category || '',
        offer: aiTopic,
        city: gmbProfile?.city || '',
        tone, language: lang,
        post_type: postType,
        post_content: generatedPost.content,
        image_url: generatedPost.image_url || null,
        scheduled_time: scheduled && scheduleDate ? scheduleDate.toISOString() : null,
        cta, cta_url: ctaUrl,
      });
      toast.success(scheduled ? 'Post scheduled!' : 'Post saved as draft!');
      setShowScheduler(false);
    } catch { toast.error('Failed to save post'); }
  };

  const handlePublish = async () => {
    if (!generatedPost || !connectionMethod || connectionMethod !== 'oauth') {
      toast.error('Please connect your Google Business Profile via OAuth first');
      return;
    }
    if (!locations || locations.length === 0) {
      toast.error('No GMB locations found.');
      return;
    }
    setPublishing(true);
    try {
      await publishToGMB(locations[0].id, generatedPost.content, generatedPost.image_url);
      toast.success('Published to Google Business Profile!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally { setPublishing(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedPost?.content || '');
    toast.success('Copied to clipboard!');
  };

  const canProceedStep2 = true; // Post type always selected
  const canProceedStep3 = contentMode === 'manual' ? manualContent.trim().length > 0 : aiTopic.trim().length > 0;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-[#E8F0FE] rounded-xl flex items-center justify-center">
          <FileText className="w-6 h-6 text-[#1A73E8]" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Create New Post</h1>
          <p className="text-[#5F6368]">Choose a post type and fill in the details for your business profile.</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="dashboard-card">
        <div className="flex items-center justify-center gap-0">
          {[
            { num: 1, label: 'Post Type' },
            { num: 2, label: 'Details' },
            { num: 3, label: 'Publish' },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center gap-1" data-testid={`step-${s.num}`}>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  step >= s.num ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#9AA0A6]'
                }`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className={`text-xs font-medium ${step >= s.num ? 'text-[#1A73E8]' : 'text-[#9AA0A6]'}`}>{s.label}</span>
              </div>
              {i < 2 && (
                <div className={`w-20 sm:w-32 h-0.5 mx-2 mt-[-14px] rounded ${step > s.num ? 'bg-[#1A73E8]' : 'bg-[#DADCE0]'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Form Area */}
        <div className="lg:col-span-2 space-y-4">
          {/* Step 1: Post Type */}
          {step === 1 && (
            <div className="dashboard-card" data-testid="step1-content">
              <h3 className="text-base font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>Select Post Type</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {POST_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setPostType(type.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      postType === type.value ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#DADCE0] hover:border-[#5F6368]'
                    }`}
                    data-testid={`post-type-${type.value}`}
                  >
                    <p className="font-semibold text-[#202124]">{type.label}</p>
                    <p className="text-xs text-[#5F6368] mt-1">{type.desc}</p>
                  </button>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={() => setStep(2)} className="btn-google flex items-center gap-2" data-testid="step1-next">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="dashboard-card space-y-5" data-testid="step2-content">
              {/* Content Mode Toggle */}
              <div>
                <h3 className="text-base font-semibold text-[#202124] mb-3" style={{ fontFamily: 'Outfit' }}>Content</h3>
                <div className="flex border border-[#DADCE0] rounded-lg overflow-hidden mb-3">
                  <button
                    onClick={() => setContentMode('manual')}
                    className={`flex-1 py-2.5 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                      contentMode === 'manual' ? 'bg-[#202124] text-white' : 'bg-white text-[#5F6368] hover:bg-[#F1F3F4]'
                    }`}
                    data-testid="content-mode-manual"
                  >
                    <Pencil className="w-4 h-4" /> Write Yourself
                  </button>
                  <button
                    onClick={() => setContentMode('ai')}
                    className={`flex-1 py-2.5 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                      contentMode === 'ai' ? 'bg-[#202124] text-white' : 'bg-white text-[#5F6368] hover:bg-[#F1F3F4]'
                    }`}
                    data-testid="content-mode-ai"
                  >
                    <Sparkles className="w-4 h-4" /> Generate with AI
                  </button>
                </div>

                {contentMode === 'manual' ? (
                  <div>
                    <textarea
                      value={manualContent}
                      onChange={(e) => setManualContent(e.target.value.slice(0, 1500))}
                      className="input-google min-h-[140px] resize-none"
                      placeholder="Write your post content here..."
                      data-testid="manual-content-input"
                    />
                    <div className="flex justify-end gap-3 mt-1 text-xs text-[#9AA0A6]">
                      <span>{charCount}/1500 characters</span>
                      <span>{wordCount} words</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <textarea
                      value={aiTopic}
                      onChange={(e) => setAiTopic(e.target.value)}
                      className="input-google min-h-[100px] resize-none"
                      placeholder="Describe what you want to post about... (e.g., Weekend special 20% off all drinks)"
                      data-testid="ai-topic-input"
                    />
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs text-[#5F6368] mb-1">Tone</label>
                        <select value={tone} onChange={(e) => setTone(e.target.value)} className="input-google text-sm" data-testid="tone-select">
                          {TONES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-[#5F6368] mb-1">Language</label>
                        <select value={lang} onChange={(e) => setLang(e.target.value)} className="input-google text-sm" data-testid="language-select">
                          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA Selection */}
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">Call to Action</label>
                <select
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  className="input-google"
                  data-testid="cta-select"
                >
                  {CTA_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {cta !== 'none' && cta !== 'call' && (
                  <input
                    type="url"
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                    className="input-google mt-2"
                    placeholder="https://your-website.com/page"
                    data-testid="cta-url-input"
                  />
                )}
              </div>

              {/* Image Section */}
              <div>
                <label className="block text-sm font-medium text-[#202124] mb-2">Image</label>
                <div className="flex gap-2 mb-3 flex-wrap">
                  {[
                    { v: 'ai', label: 'AI Generate', icon: <Sparkles className="w-3.5 h-3.5" /> },
                    { v: 'template', label: 'Creative Template', icon: <Palette className="w-3.5 h-3.5" /> },
                    { v: 'upload', label: 'Upload', icon: <Upload className="w-3.5 h-3.5" /> },
                    { v: 'none', label: 'No Image', icon: <X className="w-3.5 h-3.5" /> },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setImageMode(opt.v)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        imageMode === opt.v ? 'border-[#1A73E8] bg-[#E8F0FE] text-[#1A73E8]' : 'border-[#DADCE0] text-[#5F6368] hover:bg-[#F1F3F4]'
                      }`}
                      data-testid={`image-mode-${opt.v}`}
                    >
                      {opt.icon} {opt.label}
                    </button>
                  ))}
                </div>

                {imageMode === 'template' && (
                  <div className="space-y-3" data-testid="template-image-section">
                    {selectedTemplates.length === 0 ? (
                      <div className="p-4 bg-[#FEF7E0] rounded-lg text-sm text-[#5F6368] flex items-center gap-2">
                        <Palette className="w-4 h-4 text-[#F9AB00]" />
                        No templates selected. <a href="/dashboard/creative-templates" className="text-[#1A73E8] underline">Select templates first</a>
                      </div>
                    ) : (
                      <>
                        <select
                          value={selectedTemplateId}
                          onChange={e => setSelectedTemplateId(e.target.value)}
                          className="input-google text-sm"
                          data-testid="template-selector"
                        >
                          <option value="">Select a creative template...</option>
                          {selectedTemplates.map(t => (
                            <option key={t.id} value={t.id}>{t.name} — {t.description}</option>
                          ))}
                        </select>
                        <button
                          onClick={handleGenerateTemplateImage}
                          disabled={!selectedTemplateId || templateGenerating}
                          className="btn-google text-sm flex items-center gap-1"
                          data-testid="generate-template-btn"
                        >
                          {templateGenerating ? <><span className="animate-spin w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5" /> Generate Creative</>}
                        </button>
                        {uploadedImageUrl && imageMode === 'template' && (
                          <img
                            src={uploadedImageUrl.startsWith('data:') ? uploadedImageUrl : (uploadedImageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${uploadedImageUrl}` : uploadedImageUrl)}
                            alt="Creative"
                            className="w-full h-40 object-cover rounded-lg border border-[#DADCE0]"
                          />
                        )}
                      </>
                    )}
                  </div>
                )}

                {imageMode === 'upload' && (
                  <div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    {uploadedImageUrl ? (
                      <div className="relative">
                        <img src={uploadedImageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${uploadedImageUrl}` : uploadedImageUrl} alt="Uploaded" className="w-full h-40 object-cover rounded-lg" />
                        <button
                          onClick={() => { setUploadedImageUrl(''); fileInputRef.current?.click(); }}
                          className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg hover:bg-white"
                        >
                          <RefreshCw className="w-4 h-4 text-[#5F6368]" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full py-8 border-2 border-dashed border-[#DADCE0] rounded-lg flex flex-col items-center gap-2 text-[#5F6368] hover:border-[#1A73E8] hover:text-[#1A73E8] transition-colors"
                        data-testid="upload-image-btn"
                      >
                        <Upload className="w-6 h-6" />
                        <span className="text-sm">{uploading ? 'Uploading...' : 'Click to upload image (max 5MB)'}</span>
                      </button>
                    )}
                  </div>
                )}
                {imageMode === 'ai' && (
                  <p className="text-xs text-[#9AA0A6] flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> AI will generate a professional image based on your content
                  </p>
                )}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setStep(1)} className="btn-outline flex items-center gap-2" data-testid="step2-prev">
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => { if (contentMode === 'manual' && manualContent.trim()) { handleGenerate(); } }}
                    disabled={contentMode !== 'manual' || !manualContent.trim() || loading}
                    className={`btn-outline text-sm ${contentMode !== 'manual' ? 'hidden' : ''}`}
                    data-testid="save-draft-btn"
                  >
                    <Save className="w-4 h-4 inline mr-1" /> Save as Draft
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={loading || !canProceedStep3}
                    className="btn-google flex items-center gap-2"
                    data-testid="step2-next"
                  >
                    {loading ? (
                      <><Sparkles className="w-4 h-4 animate-spin" /> {contentMode === 'ai' ? 'Generating...' : 'Creating...'}</>
                    ) : (
                      <>Next <ChevronRight className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Publish */}
          {step === 3 && generatedPost && (
            <div className="dashboard-card space-y-4" data-testid="step3-content">
              <h3 className="text-base font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>Review & Publish</h3>

              {/* Post Content */}
              <div className="p-4 bg-[#F8F9FA] rounded-lg">
                {generatedPost.title && <h4 className="font-semibold text-[#202124] mb-2">{generatedPost.title}</h4>}
                <p className="text-[#202124] whitespace-pre-wrap">{generatedPost.content}</p>
              </div>

              {/* Image */}
              <div>
                {generatedPost.image_url ? (
                  <div className="relative group">
                    <img
                      src={generatedPost.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${generatedPost.image_url}` : generatedPost.image_url}
                      alt="Post"
                      className="w-full h-52 object-cover rounded-lg"
                    />
                    <button
                      onClick={handleRegenerateImage}
                      disabled={regeneratingImage}
                      className="absolute top-2 right-2 p-2 bg-white/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid="regenerate-image-btn"
                    >
                      <RefreshCw className={`w-4 h-4 text-[#5F6368] ${regeneratingImage ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                ) : (
                  <div className="bg-[#F8F9FA] rounded-lg p-8 text-center border border-dashed border-[#DADCE0]">
                    <ImageIcon className="w-8 h-8 mx-auto text-[#DADCE0]" />
                    <p className="text-sm text-[#5F6368] mt-2">No image</p>
                  </div>
                )}
              </div>

              {/* CTA display */}
              {cta !== 'none' && (
                <div className="flex items-center gap-2 text-sm text-[#1A73E8] font-medium">
                  {CTA_OPTIONS.find(c => c.value === cta)?.icon}
                  {ctaLabel}
                  {ctaUrl && <span className="text-[#5F6368] text-xs truncate">({ctaUrl})</span>}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
                <button onClick={() => setStep(2)} className="btn-outline flex items-center gap-2" data-testid="step3-prev">
                  <ArrowLeft className="w-4 h-4" /> Edit
                </button>
                <div className="flex flex-wrap gap-2">
                  <button onClick={handleCopy} className="btn-outline text-sm flex items-center gap-1" data-testid="copy-post-btn">
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                  <button onClick={() => handleSavePost(false)} className="btn-outline text-sm flex items-center gap-1" data-testid="save-post-btn">
                    <Save className="w-4 h-4" /> Save Draft
                  </button>
                  <Popover open={showScheduler} onOpenChange={setShowScheduler}>
                    <PopoverTrigger asChild>
                      <button className="btn-outline text-sm flex items-center gap-1" data-testid="schedule-post-btn">
                        <Calendar className="w-4 h-4" /> Schedule
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <CalendarComponent mode="single" selected={scheduleDate} onSelect={setScheduleDate} initialFocus />
                      <div className="p-3 border-t">
                        <button onClick={() => handleSavePost(true)} disabled={!scheduleDate} className="btn-google w-full text-sm py-2">
                          {scheduleDate ? `Schedule for ${format(scheduleDate, 'MMM d')}` : 'Select a date'}
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {connectionMethod === 'oauth' && (
                    <button
                      onClick={handlePublish}
                      disabled={publishing}
                      className="text-sm py-2 px-4 flex items-center gap-1 bg-[#1E8E3E] text-white rounded-lg hover:bg-[#1B7D37] transition-colors disabled:opacity-50"
                      data-testid="publish-gmb-btn"
                    >
                      <Send className="w-4 h-4" /> {publishing ? 'Publishing...' : 'Publish to GMB'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Live Preview */}
        <div className="lg:col-span-1">
          <div className="dashboard-card sticky top-24">
            <h3 className="text-sm font-semibold text-[#202124] mb-3" style={{ fontFamily: 'Outfit' }}>
              Preview ({POST_TYPES.find(t => t.value === postType)?.label})
            </h3>
            <div className="border border-[#DADCE0] rounded-lg overflow-hidden bg-white">
              {/* Preview Image Area */}
              <div className="h-44 bg-[#F1F3F4] flex items-center justify-center relative">
                {generatedPost?.image_url ? (
                  <img
                    src={generatedPost.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${generatedPost.image_url}` : generatedPost.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : uploadedImageUrl && imageMode === 'upload' ? (
                  <img
                    src={uploadedImageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${uploadedImageUrl}` : uploadedImageUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Upload className="w-8 h-8 text-[#DADCE0]" />
                )}
              </div>
              {/* Preview Text */}
              <div className="p-3">
                <p className="text-sm text-[#202124] line-clamp-4 min-h-[40px]">
                  {contentMode === 'manual' && manualContent ? manualContent :
                   generatedPost?.content ? generatedPost.content :
                   <span className="text-[#9AA0A6] italic">Your post content will appear here...</span>}
                </p>
                <div className="mt-2 pt-2 border-t border-[#F1F3F4]">
                  <p className="text-xs text-[#9AA0A6]">1 day ago</p>
                  {cta !== 'none' && (
                    <p className="text-xs text-[#1A73E8] font-medium mt-0.5">{ctaLabel}</p>
                  )}
                </div>
              </div>
            </div>
            <ul className="mt-3 text-xs text-[#9AA0A6] space-y-1">
              <li className="flex items-center gap-1"><span className="w-1 h-1 bg-[#9AA0A6] rounded-full" /> Preview shows approximate appearance</li>
              <li className="flex items-center gap-1"><span className="w-1 h-1 bg-[#9AA0A6] rounded-full" /> Actual post may vary slightly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostGeneratorPage;
